import { Toaster } from 'sonner';

import Portal from '@mui/material/Portal';
import { styled } from '@mui/material/styles';

import { snackbarClasses } from './classes';

// ----------------------------------------------------------------------

/**
 * The app's toast container, for toasts shown with `toast`. It lists them one under
 * another and fades the list out as it reaches the middle of the screen; Sonner handles timing,
 * swiping, and pausing on hover, and the cards themselves come from `ToastCard`.
 */
export function Snackbar() {
  return (
    <Portal>
      <SnackbarRoot
        expand
        gap={GAP_PX}
        offset={OFFSET_PX}
        visibleToasts={MAX_LISTED_TOASTS}
        position="top-right"
        className={snackbarClasses.root}
        toastOptions={{ unstyled: true }}
      />
    </Portal>
  );
}

// ----------------------------------------------------------------------

const GAP_PX = 12;
const OFFSET_PX = 16;
// The fade, not this count, decides how many show; it only bounds the work of a sudden burst.
const MAX_LISTED_TOASTS = 8;
const FADE_PX = 96;
// Side room inside the faded list, so card shadows are not cut off at its edges.
const SHADOW_ROOM_PX = 24;
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

const SnackbarRoot = styled(Toaster)({
  /**
   * The list spans from its corner to the middle of the screen and fades over its last stretch,
   * so a burst of toasts never covers more than half the page. Only the cards take clicks.
   */
  width: `calc(var(--width) + ${2 * SHADOW_ROOM_PX}px)`,
  height: `calc(50vh - ${OFFSET_PX}px)`,
  paddingInline: SHADOW_ROOM_PX,
  pointerEvents: 'none',
  '&[data-x-position="right"]': { marginRight: -SHADOW_ROOM_PX },
  '&[data-x-position="left"]': { marginLeft: -SHADOW_ROOM_PX },
  '&[data-y-position="top"]': {
    maskImage: `linear-gradient(to bottom, #000 calc(100% - ${FADE_PX}px), transparent)`,
  },
  '&[data-y-position="bottom"]': {
    maskImage: `linear-gradient(to top, #000 calc(100% - ${FADE_PX}px), transparent)`,
  },

  '& [data-sonner-toast]': {
    width: 'var(--width)',
    pointerEvents: 'auto',
    transition: `transform 400ms ${EASE_OUT}, opacity 300ms ease, height 400ms ${EASE_OUT}`,
  },

  // On phones Sonner spans the screen's width, so there is no side room to give. These restate
  // its own phone sizes, which the rules above would otherwise override.
  '@media (max-width: 600px)': {
    width: '100%',
    paddingInline: 0,
    marginInline: 0,
    '& [data-sonner-toast]': { width: 'calc(100% - var(--mobile-offset-left) * 2)' },
  },

  /**
   * Motion. A new toast drops a little and grows into place rather than sliding a full card's
   * height, a leaving one fades and shrinks where it is, and the rest glide to close the gap.
   * Sonner turns all of it off for people who ask their system for reduced motion.
   */
  '&[data-y-position="top"] [data-sonner-toast][data-mounted="false"]': {
    '--y': 'translateY(-12px) scale(0.96)',
  },
  '&[data-y-position="bottom"] [data-sonner-toast][data-mounted="false"]': {
    '--y': 'translateY(12px) scale(0.96)',
  },
  '& [data-sonner-toast][data-removed="true"][data-swipe-out="false"]': {
    '--y': 'translateY(calc(var(--lift) * var(--offset))) scale(0.96)',
    opacity: 0,
    transition: `transform 300ms ${EASE_OUT}, opacity 200ms ease`,
  },
});
